import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { 
  CheckCircleIcon,
  ArrowDownTrayIcon,
  ArrowRightIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { purchaseService, Purchase } from '../services/purchaseService';
import { awsService } from '../services/awsService';
import { productService } from '../services/productService';
import { formatCurrency } from '../utils';

export const PurchaseSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const purchaseId = searchParams.get('purchaseId');
  
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const verifyPurchase = async () => {
      if (!sessionId && !purchaseId) {
        setError('No purchase session found');
        setIsLoading(false);
        return;
      }

      try {
        let purchaseData: Purchase;
        
        if (sessionId) {
          purchaseData = await purchaseService.verifyPurchase(sessionId);
        } else if (purchaseId) {
          purchaseData = await purchaseService.getPurchaseById(purchaseId);
        } else {
          throw new Error('Invalid purchase session');
        }

        setPurchase(purchaseData);
      } catch (err: any) {
        console.error('Error verifying purchase:', err);
        setError(err.message || 'Failed to verify purchase');
      } finally {
        setIsLoading(false);
      }
    };

    verifyPurchase();
  }, [sessionId, purchaseId]);

  const handleDownload = async () => {
    if (!purchase || purchase.paymentStatus !== 'PAID') {
      alert('Purchase not completed or payment pending');
      return;
    }

    setIsDownloading(true);
    try {
      // Get product to get file URL
      const product = await productService.getProduct(purchase.product.id);
      
      // Generate download URL
      const { downloadUrl } = await awsService.generateDownloadUrl(product.fileUrl);
      
      // Track download
      await productService.incrementDownloads(product.id);
      
      // Open download in new tab
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading:', error);
      alert('Failed to download file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-secondary-600 text-lg">Verifying your purchase...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !purchase) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card>
          <CardContent className="text-center py-12">
            <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-secondary-900 mb-2">
              Purchase Verification Failed
            </h1>
            <p className="text-secondary-600 mb-6">
              {error || 'Unable to verify your purchase. Please contact support if you were charged.'}
            </p>
            <div className="flex items-center justify-center space-x-4">
              <Button onClick={() => navigate('/marketplace')}>
                Browse Marketplace
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPaid = purchase.paymentStatus === 'PAID';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Card>
        <CardContent className="p-8">
          {/* Success Header */}
          <div className="text-center mb-8">
            {isPaid ? (
              <>
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                  <CheckCircleIcon className="h-10 w-10 text-green-600" />
                </div>
                <h1 className="text-3xl font-bold text-secondary-900 mb-2">
                  Purchase Successful!
                </h1>
                <p className="text-secondary-600">
                  Thank you for your purchase. You can now download your product.
                </p>
              </>
            ) : (
              <>
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-4">
                  <CheckCircleIcon className="h-10 w-10 text-yellow-600" />
                </div>
                <h1 className="text-3xl font-bold text-secondary-900 mb-2">
                  Purchase Processing
                </h1>
                <p className="text-secondary-600">
                  Your payment is being processed. You'll be able to download once payment is confirmed.
                </p>
              </>
            )}
          </div>

          {/* Purchase Details */}
          <div className="bg-secondary-50 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-secondary-900 mb-4">
              Purchase Details
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-secondary-600">Product:</span>
                <span className="font-medium text-secondary-900">{purchase.product.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Amount Paid:</span>
                <span className="font-medium text-secondary-900">
                  {purchase.amountPaid 
                    ? formatCurrency(parseFloat(purchase.amountPaid))
                    : formatCurrency(parseFloat(purchase.product.price))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Payment Status:</span>
                <Badge 
                  variant={
                    purchase.paymentStatus === 'PAID' ? 'success' :
                    purchase.paymentStatus === 'PROCESSING' ? 'warning' :
                    purchase.paymentStatus === 'FAILED' ? 'error' :
                    'default'
                  }
                >
                  {purchase.paymentStatus}
                </Badge>
              </div>
              {purchase.paidAt && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">Paid At:</span>
                  <span className="font-medium text-secondary-900">
                    {new Date(purchase.paidAt).toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-secondary-600">Purchase Date:</span>
                <span className="font-medium text-secondary-900">
                  {new Date(purchase.createdAt).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            {isPaid ? (
              <>
                <Button
                  onClick={handleDownload}
                  className="w-full"
                  size="lg"
                  disabled={isDownloading}
                  loading={isDownloading}
                >
                  <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                  {isDownloading ? 'Downloading...' : 'Download Product'}
                </Button>
                <div className="flex items-center justify-center space-x-4 pt-4">
                  <Link to={`/product/${purchase.product.id}`}>
                    <Button variant="outline">
                      View Product
                      <ArrowRightIcon className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/dashboard">
                    <Button variant="outline">
                      My Purchases
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center space-y-4">
                <p className="text-secondary-600">
                  Please wait while we confirm your payment. This usually takes a few moments.
                </p>
                <div className="flex items-center justify-center space-x-4">
                  <Button onClick={() => window.location.reload()}>
                    Refresh Status
                  </Button>
                  <Link to="/dashboard">
                    <Button variant="outline">
                      Go to Dashboard
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

